import { use } from 'react';

import styled from '@emotion/styled';
import { cn } from 'cn';
import { motion } from 'framer-motion';

import { SurveyThemeContext } from '@/shared-utilities';
import { type GetProjectQuery } from '@/survey-graphql';

import SurveyAiFormsAiSection from './survey-ai-forms-ai-section';
import SurveyAiFormsAiSectionHeader from './survey-ai-forms-ai-section-header';
import SurveyAiFormsAiSectionPoweredBy from './survey-ai-forms-ai-section-powered-by';
import SurveyAiFormsFormSection from './survey-ai-forms-form-section';
import SurveyAiFormsPoweredBy from './survey-ai-forms-powered-by';
import type {
  FormAnswerValue,
  ViewMode,
  ResponseMode,
} from './survey-ai-forms.types';

const ThemedDiv = styled.div<{ themeStyles: Record<string, string> }>`
  ${(props) => props.themeStyles}
`;

interface Props {
  project: NonNullable<GetProjectQuery['project']>;
  currentQuestion:
    | NonNullable<GetProjectQuery['project']>['questions'][number]
    | null;
  currentQuestionId: string | null;
  currentQuestionIndex: number;
  totalQuestions: number;
  responseMode: ResponseMode;
  viewMode: ViewMode;
  progress: number;
  estimatedTotalTimeInSeconds: number;
  onResponseSubmit: (value: FormAnswerValue) => void;
  onNext: () => void;
  onQuestionFocus: (questionId: string) => void;
  onQuestionNavigate: (questionId: string) => void;
  saveTranscript: (questionId: string, transcript: string) => void;
  getTranscript: (questionId: string) => string;
  onViewModeToggle: () => void;
  onFormResponseSubmit: (value: FormAnswerValue) => void;
  onSubmit: () => void;
  onFormSubmit: () => void;
  registerVoiceStopCallback: (callback: (() => void) | null) => void;
  className?: string;
  showInstructions?: boolean;
  onCloseInstructions?: () => void;
  canSubmit: boolean;
  isAddingMessage?: boolean;
  isSubmitting?: boolean;
  isProcessingResponse?: boolean;
  submittedQuestionIds: Set<string>;
  validationErrors: Set<string>;
  hasTriedSubmit: boolean;
  isPreviewMode?: boolean;
  isAIFormAssistanceEnabled: boolean;
}

export default function SurveyAiFormsDesktopLayout({
  project,
  currentQuestion,
  currentQuestionId,
  currentQuestionIndex,
  totalQuestions,
  responseMode,
  viewMode,
  progress,
  estimatedTotalTimeInSeconds,
  onResponseSubmit,
  onNext,
  onQuestionFocus,
  onQuestionNavigate,
  saveTranscript,
  getTranscript,
  onViewModeToggle,
  onSubmit,
  onFormSubmit,
  className = '',
  showInstructions = false,
  onCloseInstructions,
  canSubmit,
  isAddingMessage = false,
  isSubmitting = false,
  isProcessingResponse = false,
  submittedQuestionIds,
  validationErrors,
  hasTriedSubmit,
  isPreviewMode = false,
  isAIFormAssistanceEnabled,
}: Props) {
  const { styles } = use(SurveyThemeContext);

  const themeSettings = project.themeSettings;
  const isFormMode = viewMode === 'form';
  const hasBackgroundImage = !!themeSettings?.bgImage;

  if (!isAIFormAssistanceEnabled) {
    return (
      <div
        data-test='desktop-layout'
        className={cn('flex h-screen w-full flex-col', className)}
      >
        <ThemedDiv
          className='flex w-full flex-1 overflow-hidden p-4 pb-0'
          themeStyles={styles}
          data-test='desktop-layout-form-only'
        >
          <SurveyAiFormsFormSection
            projectTitle={project.publicTitle}
            projectIntroText={project.introText}
            questions={project.questions}
            currentQuestionId={currentQuestionId}
            onQuestionFocus={onQuestionFocus}
            onSubmit={onFormSubmit}
            isExpanded={true}
            canSubmit={canSubmit}
            isSubmitting={isSubmitting}
            submittedQuestionIds={submittedQuestionIds}
            validationErrors={validationErrors}
            hasTriedSubmit={hasTriedSubmit}
            isPreviewMode={isPreviewMode}
            className='size-full rounded-4xl bg-white'
          />
        </ThemedDiv>

        <div className='flex h-8 shrink-0 items-center justify-center'>
          <SurveyAiFormsPoweredBy />
        </div>
      </div>
    );
  }

  return (
    <div
      data-test='desktop-layout'
      className={cn('flex h-screen w-full flex-col', className)}
    >
      <ThemedDiv
        className='flex w-full flex-1 overflow-hidden p-4 pb-0'
        themeStyles={styles}
      >
        {/* Left Section - AI */}
        <motion.div
          data-test='desktop-layout-left'
          data-form-mode={isFormMode}
          data-has-background-image={hasBackgroundImage}
          className='flex min-w-100 flex-col rounded-4xl rounded-tr-none bg-(image:--brand-bg-image) bg-cover bg-center bg-no-repeat data-[has-background-image=false]:bg-(--brand-bg-color)'
          initial={false}
          animate={{
            width: isFormMode ? '400px' : '100%',
            flex: isFormMode ? '0 0 auto' : '1 1 0%',
          }}
          transition={{
            duration: 0.3,
            ease: 'easeInOut',
          }}
        >
          {isFormMode ? (
            // Minimized AI view
            <div className='flex h-full min-w-100 flex-col justify-between p-8'>
              <SurveyAiFormsAiSectionHeader
                progress={progress}
                estimatedTotalTimeInSeconds={estimatedTotalTimeInSeconds}
                viewMode={viewMode}
                onViewModeToggle={onViewModeToggle}
                isAIFormAssistanceEnabled={isAIFormAssistanceEnabled}
              />

              <SurveyAiFormsAiSectionPoweredBy />
            </div>
          ) : (
            <SurveyAiFormsAiSection
              currentQuestion={currentQuestion}
              currentQuestionIndex={currentQuestionIndex}
              totalQuestions={totalQuestions}
              questions={project.questions}
              responseMode={responseMode}
              onResponseSubmit={onResponseSubmit}
              onNext={onNext}
              onQuestionFocus={onQuestionNavigate}
              saveTranscript={saveTranscript}
              viewMode={viewMode}
              showInstructions={showInstructions}
              onCloseInstructions={onCloseInstructions}
              onViewModeToggle={onViewModeToggle}
              onSubmit={onSubmit}
              progress={progress}
              estimatedTotalTimeInSeconds={estimatedTotalTimeInSeconds}
              className='h-full'
              isAddingMessage={isAddingMessage}
              isSubmitting={isSubmitting}
              isProcessingResponse={isProcessingResponse}
              getTranscript={getTranscript}
              canSubmit={canSubmit}
              isUserVoiceEnabled={project.isUserVoiceEnabled ?? false}
              isPreviewMode={isPreviewMode}
              isAIFormAssistanceEnabled={isAIFormAssistanceEnabled}
            />
          )}
        </motion.div>

        {/* Right Section - Form */}
        <motion.div
          data-test='desktop-layout-right'
          data-form-mode={isFormMode}
          className='flex size-full min-w-100 flex-col overflow-hidden bg-white'
          initial={false}
          animate={{
            width: isFormMode ? '100%' : '400px',
            flex: isFormMode ? '1 1 0%' : '0 0 auto',
          }}
          transition={{
            duration: 0.3,
            ease: 'easeInOut',
          }}
        >
          <SurveyAiFormsFormSection
            projectTitle={project.publicTitle}
            projectIntroText={project.introText}
            questions={project.questions}
            currentQuestionId={currentQuestionId}
            onQuestionFocus={onQuestionFocus}
            onSubmit={isFormMode ? onFormSubmit : onSubmit}
            isExpanded={isFormMode}
            canSubmit={canSubmit}
            isSubmitting={isSubmitting}
            submittedQuestionIds={submittedQuestionIds}
            validationErrors={validationErrors}
            hasTriedSubmit={hasTriedSubmit}
            isPreviewMode={isPreviewMode}
          />
        </motion.div>
      </ThemedDiv>

      <div className='me-100 flex h-8 shrink-0 items-center justify-center'>
        {!isFormMode && <SurveyAiFormsPoweredBy />}
      </div>
    </div>
  );
}
